import { forwardRef, type KeyboardEvent, type Ref } from 'react';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

// NOTE: 디자인시스템(@IntranetLibrary)이 비-DS 위치(components/CustomTextInput2)를 참조한다.
// 베이스를 `@IntranetLibrary` 로 옮기는 건 별도 티켓 — 현재 소비자가
// `CustomTextInput2/index.tsx` 와 `HookForm/RHFTextField` 2곳뿐이라 이동 비용은 낮다.
import BaseFormattedTextField from '@/components/CustomTextInput2/BaseFormattedTextField';
import SearchIcon from '@/public/icons/search2.svg';

import { BOX_SEARCH_SX, SEARCH_ICON_SIZE, underlineSearchSx } from './styles';

export type SearchInputVariant = 'box' | 'underline';

export type SearchInputProps = {
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** 미지정이면 `onChange('')` 로 폴백한다 */
  readonly onClear?: () => void;
  /** Enter 로 검색 확정. 한글 IME 조합 중에는 발화하지 않는다 */
  readonly onSearch?: (value: string) => void;
  /** 자동완성 드롭다운이 위/아래 화살표를 가로챌 수 있게 뚫어둔다. `onSearch` 다음에 항상 호출 */
  readonly onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  readonly placeholder?: string;
  readonly variant?: SearchInputVariant;
  readonly disabled?: boolean;
  readonly autoFocus?: boolean;
  readonly name?: string;
  readonly id?: string;
  /** 컨테이너 클릭 → 포커스, 외부에서의 프로그램적 포커스용 */
  readonly inputRef?: Ref<HTMLInputElement>;
  /**
   * 폭 지정 등. **객체만 받는다** — 베이스가 `sx={{ ...내부, ...sx }}` 로 얕게 펼치기 때문에
   * 배열/함수 형태의 `SxProps` 를 넘기면 인덱스 키가 박혀 조용히 깨진다.
   */
  readonly sx?: SystemStyleObject<Theme>;
  readonly 'aria-label'?: string;
};

/**
 * 공통 검색 인풋.
 *
 * 레포에 검색창 구현이 25개 흩어져 있고, 재사용 컴포넌트 12개 중 8개가 `useFormContext` 를
 * 강제해 폼 밖에서는 쓸 수 없었다. **이 컴포넌트는 어떤 컨텍스트에도 의존하지 않는다** —
 * 값은 전부 props 로 오간다. RHF 결합이 필요하면 `HookForm/RHFTextField` 선례대로
 * 얇은 래퍼를 따로 만든다.
 *
 * 시각 스펙의 정본은 `Approval/RelatedApproval` 의 `SearchBarRoot` 다 (`styles.ts` 참조).
 */
const SearchInput = forwardRef<HTMLDivElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      onClear,
      onSearch,
      onKeyDown,
      placeholder,
      variant = 'box',
      disabled,
      autoFocus,
      name,
      id,
      inputRef,
      sx,
      'aria-label': ariaLabel,
    },
    ref,
  ) => {
    const variantSx =
      variant === 'underline'
        ? underlineSearchSx(value.length > 0)
        : BOX_SEARCH_SX;

    /**
     * Enter 검색 + 한글 IME 가드.
     *
     * 레포가 지금 제각각이다 — `isComposing` 만 보는 곳(`LLMInputField`), `keyCode 229` 까지
     * 보는 곳(`RelatedApprovalSection`), 아예 없는 곳. 조합 중 Enter 는 "글자 확정"이지
     * "검색"이 아니므로 가드가 없으면 첫 한글 단어에서 검색이 두 번 나간다.
     * 두 선례를 합쳐 여기 한 곳에 가둔다.
     */
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (
        e.key === 'Enter' &&
        !e.nativeEvent.isComposing &&
        e.keyCode !== 229 && // 구형 IME 는 isComposing 대신 이걸 준다
        !e.repeat &&
        !e.shiftKey
      ) {
        onSearch?.(value);
      }
      onKeyDown?.(e);
    };

    return (
      <BaseFormattedTextField
        ref={ref}
        value={value}
        // 베이스는 `string | number` 로 준다. 검색어는 항상 문자열이다
        onValueChange={(next) => onChange(String(next))}
        onClear={() => (onClear ? onClear() : onChange(''))}
        onKeyDown={handleKeyDown}
        // 베이스가 clear 버튼을 먼저 깔고 그 뒤에 이걸 붙인다 → `⊗ 🔍` (시안 순서)
        extraInputAdornment={
          <SearchIcon width={SEARCH_ICON_SIZE} height={SEARCH_ICON_SIZE} />
        }
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        name={name}
        id={id}
        inputRef={inputRef}
        autoComplete="off"
        slotProps={{ htmlInput: { 'aria-label': ariaLabel } }}
        sx={{ ...variantSx, ...sx }}
        // 베이스에 절대 넘기지 않는 것들:
        // - `format`/`formatOn` — 켜지는 순간 focus 시 input 이 `type=number` 로 바뀐다
        // - `maxLength`/`helperText` — 아래에 카운터·헬퍼 영역이 생겨 레이아웃이 밀린다
        // - `notFocusedHideClear` — `.MuiInputAdornment-root` 를 통째로 숨겨서
        //   clear 뿐 아니라 **돋보기까지 같이 사라진다**
      />
    );
  },
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
